import { FileUpload } from '@consigli/facade';
import { useGetBuildingPackagesQuery } from '@consigli/hooks';
import { useProjectId } from '@consigli/hooks';
import { FC, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { BuildingPackageResponse } from './load-calculation-wizard';
import { IterationName } from '../components/iteration-name';
import { SelectLanguage } from '../components/select-language';
import { LoadCalculationUploadInfo } from '../components/upload-info-panel';
import { WizardAnalyzeButton } from '../components/wizard-analyze-button';
import { useValidateName } from '../hooks/use-validate-name';

interface LoadCalculationUploadFilesStepProps {
  localFiles: File[];
  setLocalFiles: Dispatch<SetStateAction<File[]>>;
  onLoadCalculationPackageCreation: () => BuildingPackageResponse;
}

export const LoadCalculationUploadFilesStep: FC<LoadCalculationUploadFilesStepProps> = ({
  localFiles,
  setLocalFiles,
  onLoadCalculationPackageCreation,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const validateName = useValidateName(useGetBuildingPackagesQuery, {
    projectId,
    page: 'all',
  });

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center space-y-6">
      <div className="w-full">
        <LoadCalculationUploadInfo />
      </div>
      <div className="w-full">
        <IterationName
          validateName={validateName}
          label={t('package-creation.analysis-name')}
          errorMessage={`${t('package-creation.analysis-name-empty')}*`}
        />
      </div>

      <div className="w-full">
        <SelectLanguage />
      </div>

      <div className="w-full">
        <p className="mb-4 mt-4 text-base font-semibold text-day-neutral-subtle">
          {t('package-creation.upload-files')}
        </p>
        <FileUpload
          files={localFiles}
          setFiles={setLocalFiles}
          dropAreaLabel={t('document-upload.drag-n-drop-here')}
          dropAreaClassName="mt-4 h-48 w-full"
          fileButtonLabel={t('document-upload.browse-files')}
          folderButtonLabel={t('document-upload.browse-folders')}
          uploadListLabel={t('document-upload.files')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
        >
          <div className="fc-w-full">
            <WizardAnalyzeButton
              localFiles={localFiles}
              type="submit"
              onAnalyze={onLoadCalculationPackageCreation}
            />
          </div>
        </FileUpload>
      </div>
    </div>
  );
};
